/**
 * Productos en renting: hardware que el cliente paga a plazos.
 *
 * Vive suelto y sin importar nada, como el resto de `lib/`.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 24-sep-2026: hardware con una cuota distinta según los meses. Lo
 * financia una entidad ajena, así que la regla que gobierna todo esto es:
 *
 *     UN RENTING NO SE FACTURA DESDE AQUÍ.
 *
 * Solo se ve en el presupuesto, para que el cliente sepa lo que le cuesta al
 * mes. De ahí que una línea de renting se guarde con `subtotal: 0`: no le
 * facturamos ese equipo, y así cualquier suma del proyecto que se olvide del
 * renting suma cero en vez de colarle un cobro.
 *
 * Réplica de `renting.py`, redondeos incluidos, por la misma razón que
 * `cobros.js` e `impuestos.js`: si la pantalla dijera una cuota y el servidor
 * guardara otra, nadie se fiaría de la pantalla.
 */

/**
 * Valor histórico del tipo de producto `renting`, del 24-sep-2026, cuando el
 * renting era una categoría con su propia pestaña. Angel, al día siguiente:
 * "esos productos métemelos como productos y no como una categoría aparte".
 *
 * Tenía razón, y por una razón de fondo: el renting no es lo que un producto
 * ES, sino cómo se PAGA. El mismo TPV se vende o se alquila. Como categoría
 * obligaba a duplicar el catálogo y dejaba el producto fuera de las facturas.
 *
 * Ahora manda el dato: un producto con tabla de `plazos` se puede alquilar, y
 * una LÍNEA con `renting_meses` es un alquiler. Esto solo sobrevive para que
 * los productos dados de alta aquel día sigan apareciendo donde toca.
 */
export const TIPO = 'renting';

/** En qué pestaña del presupuesto vive cada tipo de producto. */
const SECCION_DE_TIPO = {
  license: 'licenses',
  service: 'services',
  equipment: 'equipment',
  [TIPO]: 'equipment',
};

/**
 * El tipo con el que se trata este producto.
 *
 * Traduce el `renting` histórico a `equipment`. Sin esto, los productos
 * creados el 24-sep no saldrían en ninguna sección: se quedarían huérfanos
 * sin dar ningún error, que es como peor se descubre.
 */
export function tipoEfectivo(producto) {
  const tipo = producto?.type;
  return tipo === TIPO ? 'equipment' : (tipo || 'equipment');
}

/** La sección del presupuesto donde va este producto. */
export function seccionDe(producto) {
  return SECCION_DE_TIPO[tipoEfectivo(producto)] || 'equipment';
}

/** Fuera de esta horquilla es una errata al teclear, no un plazo. */
export const MESES_MINIMO = 1;
export const MESES_MAXIMO = 120;

/**
 * Si este producto SE PUEDE ofrecer en renting.
 *
 * Lo contesta la tabla de plazos, no el tipo: un producto con plazos pactados
 * se puede alquilar. El mismo TPV se vende o se alquila.
 */
export function tienePlazos(producto) {
  return normalizarPlazos(producto?.plazos).length > 0;
}

/**
 * Si esta línea de presupuesto ES un renting.
 *
 * Lo decide `renting_meses`, que es lo que se eligió al ponerla: el mismo
 * producto puede estar vendido en un presupuesto y alquilado en otro.
 *
 * Es LA función de este fichero. De ella depende que una cuota que paga una
 * financiera no entre en el total que le cobramos al cliente.
 */
export function esLineaDeRenting(linea) {
  const meses = Number(linea?.renting_meses);
  return Number.isInteger(meses) && meses > 0;
}

/** Las líneas que SÍ se le facturan al cliente: todas menos las de renting. */
export function lineasFacturables(items) {
  return (items || []).filter((l) => !esLineaDeRenting(l));
}

/**
 * Lo que JavaScript convierte a número y Python no.
 *
 * `Number(null)`, `Number('')` y `Number([])` valen 0, no NaN. Sin este filtro
 * un plazo a medio teclear -- `{meses: 24, cuota: null}` -- se guardaba como
 * una cuota de 0 €, que significa "mes gratis": una cosa muy distinta de "aún
 * no he puesto el precio". Y una línea con `quantity: null` salía a 0 €/mes en
 * vez de contar una unidad, como sí hace `renting.py`.
 *
 * Los dos módulos tienen que decir lo mismo o la pantalla miente.
 */
function esNumerico(valor) {
  if (typeof valor === 'number') return Number.isFinite(valor);
  if (typeof valor !== 'string') return false;
  return valor.trim() !== '' && Number.isFinite(Number(valor));
}

function entero(valor) {
  if (!esNumerico(valor)) return null;
  const n = Number(valor);
  return Number.isInteger(n) ? n : null;
}

function dinero(valor) {
  if (!esNumerico(valor)) return null;
  return Math.round(Number(valor) * 100) / 100;
}

/**
 * La tabla de plazos limpia y ordenada de menos meses a más.
 *
 * Ordenar aquí evita que cada pantalla lo haga a su manera. Descarta lo que
 * no sirve en vez de quejarse: para el error está `motivoParaNoGuardar`.
 */
export function normalizarPlazos(plazos) {
  const limpios = [];
  const vistos = new Set();
  for (const plazo of plazos || []) {
    if (!plazo || typeof plazo !== 'object') continue;
    const meses = entero(plazo.meses);
    const cuota = dinero(plazo.cuota);
    if (meses === null || cuota === null || meses <= 0 || cuota < 0) continue;
    if (vistos.has(meses)) continue;
    vistos.add(meses);
    limpios.push({ meses, cuota });
  }
  return limpios.sort((a, b) => a.meses - b.meses);
}

/**
 * Por qué esta tabla de plazos no se puede guardar. Null cuando sí se puede.
 *
 * Un renting SIN plazos no sirve para nada: al añadirlo a un presupuesto no
 * habría cuota que elegir. Y se comprueba al guardar el producto, no al hacer
 * el presupuesto, para que el error salga donde está la errata.
 *
 * Una cuota de 0 € SÍ vale: un mes de cortesía se pacta, y prohibirlo obliga
 * a inventarse un céntimo.
 */
export function motivoParaNoGuardar(plazos) {
  if (!plazos || plazos.length === 0) {
    return 'Un producto de renting necesita al menos un plazo con su cuota.';
  }
  const vistos = new Set();
  for (const plazo of plazos) {
    const meses = entero(plazo?.meses);
    if (meses === null) return 'Hay un plazo sin número de meses.';
    if (meses < MESES_MINIMO || meses > MESES_MAXIMO) {
      return `El plazo de ${meses} meses no es razonable: tiene que estar `
        + `entre ${MESES_MINIMO} y ${MESES_MAXIMO}.`;
    }
    if (vistos.has(meses)) return `El plazo de ${meses} meses está repetido.`;
    vistos.add(meses);

    const cuota = dinero(plazo?.cuota);
    if (cuota === null) return `El plazo de ${meses} meses no tiene cuota.`;
    if (cuota < 0) return `La cuota de ${meses} meses no puede ser negativa.`;
  }
  return null;
}

/**
 * La cuota mensual de este producto para este plazo. Null si no lo tiene.
 *
 * Null es información, no un fallo: significa "este plazo no está pactado con
 * la financiera". Quien pregunta debe negarse a poner la línea, no interpolar.
 */
export function cuotaDe(producto, meses) {
  const buscado = entero(meses);
  if (buscado === null) return null;
  const plazo = normalizarPlazos(producto?.plazos).find((p) => p.meses === buscado);
  return plazo ? plazo.cuota : null;
}

/**
 * Lo que cuesta al mes una línea: la cuota por las unidades.
 *
 * El descuento NO se aplica: lo pactado con la financiera es la cuota, y
 * rebajarla por nuestra cuenta diría en el presupuesto un precio que el
 * cliente no va a pagar.
 */
export function cuotaDeLaLinea(linea) {
  const cuota = dinero(linea?.unit_price) || 0;
  let unidades = entero(linea?.quantity);
  if (unidades === null || unidades < 0) unidades = 1;
  return Math.round(cuota * unidades * 100) / 100;
}

/** Lo que suma el renting entero: la cuota del mes por los meses. */
export function totalDelPlazo(linea) {
  const meses = entero(linea?.renting_meses) || 0;
  return Math.round(cuotaDeLaLinea(linea) * Math.max(0, meses) * 100) / 100;
}

/** El renting de un presupuesto, para enseñarlo de un vistazo. */
export function resumenRenting(items) {
  const lineas = (items || []).filter(esLineaDeRenting);
  const meses = [...new Set(
    lineas.map((l) => entero(l?.renting_meses)).filter((m) => m !== null),
  )].sort((a, b) => a - b);
  return {
    hay: lineas.length > 0,
    lineas: lineas.length,
    // Lo único que Angel dijo que le importa al cliente: "lo que quiere ver
    // el cliente es que le cuesta".
    cuotaMes: Math.round(lineas.reduce((s, l) => s + cuotaDeLaLinea(l), 0) * 100) / 100,
    totalPlazo: Math.round(lineas.reduce((s, l) => s + totalDelPlazo(l), 0) * 100) / 100,
    meses,
  };
}

/**
 * Pasa una línea a renting, o la devuelve a compra si `meses` es vacío.
 *
 * Una sola función porque la ida y la vuelta tienen que ser exactas: lo que
 * pone el renting (cuota en `unit_price`, subtotal a 0) hay que saber
 * deshacerlo entero, o quedaría un equipo de 1.200 € vendido a 45 € sin que
 * nadie lo note.
 *
 * Devuelve la línea sin tocar si el plazo no está pactado: inventarse un
 * precio sería peor que no cambiar nada.
 */
export function pasarARenting(linea, producto, meses) {
  const nueva = { ...(linea || {}) };

  if (meses === null || meses === undefined || meses === '' || Number(meses) === 0) {
    // Vuelve a ser una compra: recupera el precio del catálogo.
    const precio = dinero(producto?.price) || 0;
    const unidades = entero(nueva.quantity) || 1;
    const descuento = dinero(nueva.discount) || 0;
    nueva.renting_meses = null;
    nueva.unit_price = precio;
    nueva.subtotal = Math.round(precio * unidades * (1 - descuento / 100) * 100) / 100;
    return nueva;
  }

  const cuota = cuotaDe(producto, meses);
  if (cuota === null) return nueva;

  nueva.renting_meses = entero(meses);
  // El precio de UN mes. Ver la cabecera del fichero.
  nueva.unit_price = cuota;
  // Cero: no le facturamos este equipo al cliente.
  nueva.subtotal = 0;
  // Un renting no se descuenta: lo pactado con la financiera es la cuota.
  nueva.discount = 0;
  return nueva;
}

/**
 * Construye la línea de presupuesto de un producto alquilado.
 *
 * Conserva el TIPO REAL del producto: un TPV alquilado sigue siendo un equipo
 * y sale con los equipos. Lo que lo hace renting es `renting_meses`.
 *
 * Devuelve null si el plazo no está pactado, en vez de inventarse un precio.
 */
export function lineaDeRenting(producto, meses, cantidad = 1) {
  if (cuotaDe(producto, meses) === null) return null;

  let unidades = entero(cantidad);
  if (unidades === null || unidades < 1) unidades = 1;

  return pasarARenting({
    product_id: producto.id,
    product_ref: producto.ref || '',
    product_name: producto.name,
    product_type: tipoEfectivo(producto),
    product_description: producto.description || '',
    // La cuota la pone la financiera; tocarla descuadra el contrato.
    allow_custom_line: false,
    quantity: unidades,
    unit_price: 0,
    discount: 0,
    subtotal: 0,
    section: seccionDe(producto),
  }, producto, meses);
}

/**
 * Las opciones del desplegable de pago de una línea: comprar o alquilar.
 *
 * La compra solo se ofrece si el producto tiene precio. Si Angel solo alquila
 * ese TPV y no lo vende, su precio será 0 y ofrecer "Compra — 0,00 €" sería
 * invitar a poner en un presupuesto un equipo regalado.
 */
export function formasDePago(producto) {
  const opciones = [];
  if ((dinero(producto?.price) || 0) > 0) {
    opciones.push({ meses: null, etiqueta: `Compra · ${dinero(producto.price).toFixed(2)} €` });
  }
  for (const plazo of normalizarPlazos(producto?.plazos)) {
    opciones.push({
      meses: plazo.meses,
      etiqueta: `Renting ${plazo.meses}m · ${plazo.cuota.toFixed(2)} €/mes`,
    });
  }
  return opciones;
}

/** Cómo se lee un plazo en pantalla. */
export function etiquetaDePlazo(plazo) {
  return `${plazo.meses} meses · ${plazo.cuota.toFixed(2)} €/mes`;
}

export default resumenRenting;
